import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import type { PaymentMethod } from "@stripe/stripe-js";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import "./PremiumCheckoutLander.scss";

import LoadingSpinner from "../../modals/Spinners/LoadingSpinner";
import { useAuthContext } from "../../../../hooks/context/AuthContext";
import { analyticsSetUser, analyticsTrack } from "../../../../utils/amplitude";

import PremiumCheckoutHeader from "./PremiumCheckoutHeader";
import PremiumCheckoutPlanCard from "./PremiumCheckoutPlanCard";
import PremiumCheckoutApplePayButton from "./PremiumCheckoutApplePayButton";
import {
    getStripePriceId,
    maskEmailForBanner,
    PREMIUM_CHECKOUT_LEGAL_LINKS,
    PREMIUM_CHECKOUT_PLANS,
    readEmailFromSearch,
    type PremiumCheckoutPlan,
} from "./premiumCheckoutConfig";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

type PremiumCheckoutLanderProps = {
    plan: PremiumCheckoutPlan;
};

/**
 * New iOS-app checkout landers (Apple Pay only).
 * Backend subscription create is stubbed until product IDs / API are ready.
 */
const PremiumCheckoutLander: React.FC<PremiumCheckoutLanderProps> = ({
    plan,
}) => {
    const navigate = useNavigate();
    const { user_id } = useParams<{ user_id: string }>();
    const userId = user_id ?? "";
    const [searchParams] = useSearchParams();
    const config = PREMIUM_CHECKOUT_PLANS[plan];

    const {
        eligibility,
        fetchEligibility,
        postSubscriptionsCreate,
        getStripeSubscriptionInfo,
        token,
    } = useAuthContext();

    const [showPaymentSpinner, setShowPaymentSpinner] = useState(false);
    const [paymentFailed, setPaymentFailed] = useState(false);
    const viewedEventSentRef = useRef(false);

    const forceState = useMemo(() => {
        const raw = (searchParams.get("force") || "").trim().toLowerCase();
        if (raw === "oops" || raw === "checkout" || raw === "success") {
            return raw;
        }
        return null;
    }, [searchParams]);

    // Stub subscription create until Stripe product IDs are wired.
    // Eligibility still runs against the existing API.
    const stubPaymentCreate = !getStripePriceId(plan);

    const rawEmail = useMemo(
        () => readEmailFromSearch(searchParams),
        [searchParams],
    );
    const emailDisplay = useMemo(
        () => (rawEmail ? maskEmailForBanner(rawEmail) : ""),
        [rawEmail],
    );

    const successPath = useMemo(() => {
        const params = new URLSearchParams();
        if (rawEmail) params.set("user_email", rawEmail);
        if (userId) params.set("user_id", userId);
        const qs = params.toString();
        return qs
            ? `/premium-checkout-success/?${qs}`
            : "/premium-checkout-success/";
    }, [rawEmail, userId]);

    const oopsPath = useMemo(() => {
        const params = new URLSearchParams();
        if (rawEmail) params.set("user_email", rawEmail);
        const qs = params.toString();
        return qs ? `/premium-already/?${qs}` : "/premium-already/";
    }, [rawEmail]);

    const [eligibilityReady, setEligibilityReady] = useState(
        forceState === "checkout",
    );

    useEffect(() => {
        if (forceState === "oops") {
            navigate(oopsPath, { replace: true });
            return;
        }
        if (forceState === "success") {
            navigate(successPath, { replace: true });
        }
    }, [forceState, navigate, oopsPath, successPath]);

    useEffect(() => {
        if (forceState === "checkout") {
            setEligibilityReady(true);
            return;
        }
        if (!userId) {
            setEligibilityReady(true);
            return;
        }

        let cancelled = false;
        setEligibilityReady(false);

        fetchEligibility(userId).finally(() => {
            if (!cancelled) setEligibilityReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, [userId, fetchEligibility, forceState]);

    useEffect(() => {
        if (forceState === "checkout") return;
        if (!eligibilityReady) return;
        if (eligibility === false) {
            navigate(oopsPath, { replace: true });
        }
    }, [eligibility, eligibilityReady, navigate, oopsPath, forceState]);

    useEffect(() => {
        const canTrack =
            forceState === "checkout" ||
            (eligibilityReady && eligibility === true);

        if (!canTrack || viewedEventSentRef.current) return;

        viewedEventSentRef.current = true;
        if (userId) analyticsSetUser(userId);
        analyticsTrack("premiumCheckoutWebPageViewed", {
            path: window.location.pathname,
            plan,
            ts: Date.now(),
            fullQuery: window.location.search,
            stubPaymentCreate,
        });
    }, [
        eligibility,
        eligibilityReady,
        userId,
        plan,
        forceState,
        stubPaymentCreate,
    ]);

    const handlePaymentMethodCreated = useCallback(
        (pm: PaymentMethod) => {
            setShowPaymentSpinner(true);
            setPaymentFailed(false);

            const priceId = getStripePriceId(plan);

            const onSuccess = () => {
                if (userId) analyticsSetUser(userId);
                analyticsTrack("boughtIAP", {
                    path: window.location.pathname,
                    plan,
                    ts: Date.now(),
                    fullQuery: window.location.search,
                    deferred: stubPaymentCreate,
                });
                navigate(successPath, { replace: true });
                setShowPaymentSpinner(false);
                if (token) {
                    getStripeSubscriptionInfo(token);
                }
            };

            const onFail = (error?: string) => {
                if (error === "user is not eligible") {
                    navigate(oopsPath, { replace: true });
                    return;
                }
                setPaymentFailed(true);
                setShowPaymentSpinner(false);
            };

            // Backend not ready: complete Apple Pay UI flow and land on Congrats.
            if (stubPaymentCreate || !priceId) {
                console.info(
                    "[PremiumCheckout] Stubbing subscription create — awaiting backend product IDs.",
                    { plan, paymentMethodId: pm.id, priceId },
                );
                onSuccess();
                return;
            }

            postSubscriptionsCreate(
                userId,
                priceId,
                pm.id,
                onSuccess,
                onFail,
            );
        },
        [
            plan,
            stubPaymentCreate,
            userId,
            navigate,
            successPath,
            oopsPath,
            postSubscriptionsCreate,
            getStripeSubscriptionInfo,
            token,
        ],
    );

    const showCheckout =
        forceState === "checkout" ||
        (eligibilityReady && eligibility === true);

    const showLoading =
        forceState !== "checkout" && !eligibilityReady && Boolean(userId);

    const renderDisclaimer = () => {
        const parts = config.disclaimer.split("Terms and Privacy Policy.");
        return (
            <p className="PremiumCheckoutLander__disclaimer">
                {parts[0]}
                <a href={PREMIUM_CHECKOUT_LEGAL_LINKS.terms}>Terms</a>
                {" and "}
                <a href={PREMIUM_CHECKOUT_LEGAL_LINKS.privacy}>
                    Privacy Policy
                </a>
                .
            </p>
        );
    };

    return (
        <section
            className="PremiumCheckoutLander"
            aria-label={`Premium ${config.planLabel} Checkout`}
        >
            <PremiumCheckoutHeader emailDisplay={emailDisplay} />

            <LoadingSpinner
                className="PremiumCheckoutLander__spinner"
                raised={showPaymentSpinner}
                fullPage
            />

            <div className="PremiumCheckoutLander__content">
                <h1 className="PremiumCheckoutLander__title">Checkout</h1>

                <PremiumCheckoutPlanCard config={config} />

                {showLoading ? (
                    <div className="PremiumCheckoutLander__loading">
                        Loading...
                    </div>
                ) : null}

                {showCheckout ? (
                    <Elements stripe={stripePromise} options={{ locale: "auto" }}>
                        <PremiumCheckoutApplePayButton
                            config={config}
                            disabled={showPaymentSpinner}
                            allowSimulateCheckout={stubPaymentCreate}
                            onSimulateCheckout={() => {
                                // Local/UI path: no real PaymentMethod when Apple Pay can't open (HTTP, etc.)
                                console.info(
                                    "[PremiumCheckout] Simulating Apple Pay checkout (no live wallet).",
                                    { plan },
                                );
                                setShowPaymentSpinner(true);
                                if (userId) analyticsSetUser(userId);
                                analyticsTrack("boughtIAP", {
                                    path: window.location.pathname,
                                    plan,
                                    ts: Date.now(),
                                    fullQuery: window.location.search,
                                    deferred: true,
                                    simulated: true,
                                });
                                navigate(successPath, { replace: true });
                                setShowPaymentSpinner(false);
                            }}
                            errorText={
                                paymentFailed
                                    ? "Payment failed, please try again later."
                                    : ""
                            }
                            onPaymentMethodCreated={handlePaymentMethodCreated}
                        />
                    </Elements>
                ) : null}

                {renderDisclaimer()}
            </div>
        </section>
    );
};

export default PremiumCheckoutLander;
